import {
  Factory,
  I18nString,
  Settings,
  Condition,
  Identifiable,
  Deletable,
  Modifiable,
  LockerDimensionIds,
  PriceCategoryId,
} from '..'
import { LastHardwareContact } from './last-hardware-contact'
import { IssueReport } from './issue-report'
import { getRaw, hasRaw, rememberPayload } from '../../_utils/fcdc-v2'
import { assignJsonKey, isPlainObject } from '../../_utils/fcdc-v2/json'

export enum Led {
  Unprovisioned = 'Unprovisioned',
  Available = 'Available',
  Unavailable = 'Unavailable',
  Maintenance = 'Maintenance',
}

export class LedStatus {
  status: Led
  constructor(status: Led) {
    this.status = status
  }
}

export class Locker implements Identifiable, Deletable, Modifiable {
  id: string
  siteId: string
  locationId: string
  priceCategoryId: string
  dimensionsId: string
  condition: Condition
  name?: string
  paymentTerminal?: PaymentTerminal
  settings?: Settings
  lockStatus: LockStatus
  lockCount: number // 1 is default if not specified
  ledCount: number // 1 is default if not specified
  led: LedStatus
  deleted: boolean
  created: Date
  modified: Date
  qrCode?: string
  issueReports: IssueReport[]

  constructor(json: any) {
    this.id = json.id || ''
    this.siteId = json.siteId
    this.locationId = json.locationId
    this.priceCategoryId = json.priceCategoryId
    this.dimensionsId = json.dimensionsId
    this.condition = json.condition
    this.lockStatus = json.lockStatus
    this.lockCount = json.lockCount
    this.ledCount = json.ledCount
    this.led = json.led
    this.settings = json.settings ? new Settings(json.settings) : undefined
    this.name = json.name
    this.paymentTerminal = json.paymentTerminal
      ? new PaymentTerminal(json.paymentTerminal)
      : undefined
    this.deleted = json.deleted ? Boolean(json.deleted) : false
    this.created = new Date(json.created)
    this.modified = new Date(json.modified)
    this.qrCode = json.qrCode
    this.issueReports = json.issueReports
      ? json.issueReports.map(
          (issueReport: any) => new IssueReport(issueReport)
        )
      : []
    rememberPayload(this, json)
  }

  static getFactory(): Factory<Locker> {
    return new (class implements Factory<Locker> {
      make(json: any): Locker {
        return new Locker(json)
      }

      getTableName(): string {
        return 'locker'
      }
    })()
  }

  static getUrl(siteId: string, lockerId?: string): string {
    return `/sites/${siteId}/lockers` + (lockerId ? '/' + lockerId : '')
  }
}

export class Dimensions implements Identifiable, Modifiable, Deletable {
  measurementSystem: DimensionsType
  id: string
  names: I18nString[]
  note?: string
  created: Date
  modified: Date
  height: number
  width: number
  depth: number
  priceCategoryId: string
  deleted: boolean

  constructor(json: any) {
    this.measurementSystem = json.measurementSystem
    this.id = json.id
    this.names = json.names
      ? json.names.map((name: any) => new I18nString(name))
      : []
    this.note = json.note
    this.created = new Date(json.created)
    this.modified = new Date(json.modified)
    this.height = json.height
    this.width = json.width
    this.depth = json.depth
    this.priceCategoryId =
      this.lookupPriceCategoryId(json.id) ?? json.priceCategoryId ?? json.id
    this.deleted = json.deleted ? Boolean(json.deleted) : false
    rememberPayload(this, json)
  }

  static getFactory(): Factory<Dimensions> {
    return new (class implements Factory<Dimensions> {
      make(json: any): Dimensions {
        switch (json.measurementSystem) {
          case DimensionsType.Metric:
            return new MetricDimensions(json)
          case DimensionsType.Imperial:
            return new ImperialDimensions(json)
          default:
            return new Dimensions(json)
        }
      }

      getTableName(): string {
        return 'locker_dimensions'
      }
    })()
  }

  static getUrl(dimensionsId?: string): string {
    return `/locker_dimensions` + (dimensionsId ? '/' + dimensionsId : '')
  }

  private lookupPriceCategoryId(id: string): string | undefined {
    switch (id) {
      case LockerDimensionIds.V3_Small:
        return PriceCategoryId.V3_Small
      case LockerDimensionIds.V3_Medium:
        return PriceCategoryId.V3_Medium
      case LockerDimensionIds.V3_Large:
        return PriceCategoryId.V3_Large
      case LockerDimensionIds.V3_ExtraLarge:
        return PriceCategoryId.V3_ExtraLarge
      case LockerDimensionIds.XXS:
        return PriceCategoryId.XXS
      case LockerDimensionIds.XS:
        return PriceCategoryId.XS
      case LockerDimensionIds.S:
        return PriceCategoryId.S
      case LockerDimensionIds.M:
        return PriceCategoryId.M
      case LockerDimensionIds.L:
        return PriceCategoryId.L
      case LockerDimensionIds.XL:
        return PriceCategoryId.XL
      case LockerDimensionIds.XXL:
        return PriceCategoryId.XXL
      case LockerDimensionIds.SKI_BOXES:
        return PriceCategoryId.SKI_BOXES

      default:
        return undefined
    }
  }
}

const TERMINAL_WIRE_KEYS = new Set([
  'type',
  'hardwareId',
  'lockerIds',
  'lastHardwareContact',
  'hideFromWatchdog',
  'tabletDeviceId',
  'terminalHardwareId',
  'terminalIpAddress',
  'terminalLastHardwareContact',
  'tabletLastHardwareContact',
])

export enum PaymentTerminalComponentType {
  PaymentTerminal = 'PaymentTerminal',
  Tablet = 'Tablet',
}

export enum PaymentTerminalType {
  UnprovisionedMonoDevice = 'UnprovisionedMonoDevice',
  UnprovisionedDuoDevice = 'UnprovisionedDuoDevice',
  MonoDevice = 'MonoDevice',
  DuoDevice = 'DuoDevice',
}

export type PaymentTerminalTypeChangeRequest =
  | { type: PaymentTerminalType.MonoDevice; hardwareId: string }
  | {
      type: PaymentTerminalType.DuoDevice
      terminalHardwareId: string
      tabletDeviceId: string
    }

export class PaymentTerminal {
  type?: PaymentTerminalType
  hardwareId?: string
  lockerIds: string[]
  lastHardwareContact?: LastHardwareContact
  hideFromWatchdog: boolean
  tabletDeviceId?: string
  terminalHardwareId?: string
  terminalIpAddress?: string
  terminalLastHardwareContact?: LastHardwareContact
  tabletLastHardwareContact?: LastHardwareContact

  constructor(json: any) {
    this.type = json.type
    this.hardwareId = json.hardwareId
    this.lockerIds = json.lockerIds ?? []
    this.lastHardwareContact = json.lastHardwareContact
      ? new LastHardwareContact(json.lastHardwareContact)
      : undefined
    this.hideFromWatchdog = json.hideFromWatchdog
      ? Boolean(json.hideFromWatchdog)
      : false
    this.tabletDeviceId = json.tabletDeviceId
    this.terminalHardwareId = json.terminalHardwareId
    this.terminalIpAddress = json.terminalIpAddress
    this.terminalLastHardwareContact = json.terminalLastHardwareContact
      ? new LastHardwareContact(json.terminalLastHardwareContact)
      : undefined
    this.tabletLastHardwareContact = json.tabletLastHardwareContact
      ? new LastHardwareContact(json.tabletLastHardwareContact)
      : undefined
    rememberPayload(this, json)
  }

  /**
   * An unprovisioned terminal is stored as `{ type }` plus keys this client does not know.
   * View defaults such as `lockerIds: []` must not be written back.
   */
  wireUnprovisioned(): Record<string, unknown> | undefined {
    if (!this.isUnprovisioned() || !this.type) {
      return undefined
    }

    const body: Record<string, unknown> = {}
    const raw = hasRaw(this) ? getRaw(this) : undefined
    if (isPlainObject(raw)) {
      for (const key of Object.keys(raw)) {
        if (TERMINAL_WIRE_KEYS.has(key)) {
          continue
        }
        assignJsonKey(body, key, raw[key])
      }
    }
    body['type'] = this.type
    return body
  }

  isUnprovisioned(): boolean {
    return (
      this.type === PaymentTerminalType.UnprovisionedMonoDevice ||
      this.type === PaymentTerminalType.UnprovisionedDuoDevice
    )
  }

  getHardwareId(): string | undefined {
    return this.hardwareId ?? this.terminalHardwareId
  }

  getKind(): PaymentTerminalType.MonoDevice | PaymentTerminalType.DuoDevice {
    if (
      this.type === PaymentTerminalType.UnprovisionedDuoDevice ||
      this.type === PaymentTerminalType.DuoDevice ||
      this.tabletDeviceId !== undefined ||
      this.terminalHardwareId !== undefined
    ) {
      return PaymentTerminalType.DuoDevice
    }

    return PaymentTerminalType.MonoDevice
  }

  getLastHardwareContact(): LastHardwareContact | undefined {
    return (
      this.lastHardwareContact ??
      this.terminalLastHardwareContact ??
      this.tabletLastHardwareContact
    )
  }

  isDuo(): boolean {
    return this.getKind() === PaymentTerminalType.DuoDevice
  }

  getTerminalLastHardwareContact(): LastHardwareContact | undefined {
    return this.terminalLastHardwareContact ?? this.lastHardwareContact
  }

  getTabletLastHardwareContact(): LastHardwareContact | undefined {
    return this.tabletLastHardwareContact
  }
}

export class MetricDimensions extends Dimensions {
  constructor(json: any) {
    super(json)
  }
}

export class ImperialDimensions extends Dimensions {
  constructor(json: any) {
    super(json)
  }
}

export enum DimensionsType {
  Metric = 'Metric',
  Imperial = 'Imperial',
}

export enum LockStatus {
  Open = 'Open',
  Closed = 'Closed',
}
